import type { IScope } from "angular";
import { cloneDeep, isEmpty } from "lodash";
import { finalize } from "rxjs/operators";

import type { ICountry, ILanguage, IPartner, IResponseWithLoading, IrMap } from "App/Modules/Models";
import type {
    ICountriesRepository,
    ILocalesRepository,
    IPartnersRepository,
    ITimeZonesRepository,
} from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditPartnerSettingsComponentController {
    public static $inject: string[] = [
        "$scope",
        "$translate",
        "mmPartnersRepository",
        "mmTimeZonesRepository",
        "irCountriesRepository",
        "irLocalesRepository",
    ];

    public original: IPartner;

    public edited: IPartner;

    public defaultLanguage: ILanguage;

    public defaultCountry: ICountry;

    public countries: IResponseWithLoading<ICountry[]>;

    public languages: IResponseWithLoading<ILanguage[]>;

    public timeZones: IResponseWithLoading<IrMap<string, string>>;

    public locales: IResponseWithLoading<string[]>;

    public resolve: IFormContainerResolve<IPartner, void>;

    public editResponse: IResponseWithLoading<IPartner>;

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly partnersRepository: IPartnersRepository,
        private readonly timeZonesRepository: ITimeZonesRepository,
        private readonly countriesRepository: ICountriesRepository,
        private readonly localesRepository: ILocalesRepository,
    ) {}

    public $onInit() {
        this.original = this.resolve.formData;
        this.edited = cloneDeep(this.original);

        this.defaultLanguage = {
            key: this.original.defaultLanguageKey,
        } as ILanguage;

        this.defaultCountry = {
            alpha2: this.original.defaultCountryName,
            id: this.original.defaultCountryId,
        } as ICountry;

        extendWithLoadingAndFail(this.localesRepository.getAllLocales())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.locales = response;
                },
                (error) => {
                    this.locales = error;
                },
            );

        extendWithLoadingAndFail(this.partnersRepository.getPartnerLanguages(this.original.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.languages = response;

                    if (!this.languages.isLoading && this.languages.data) {
                        // eslint-disable-next-line no-restricted-syntax
                        for (const language of this.languages.data) {
                            language.translated = this.$translate.instant(`Entities.Language.${language.key}`);
                        }
                    }
                },
                (error) => {
                    this.languages = error;
                },
            );

        extendWithLoadingAndFail(this.timeZonesRepository.getAllTimeZones())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.timeZones = response;
                },
                (error) => {
                    this.timeZones = error;
                },
            );

        extendWithLoadingAndFail(this.countriesRepository.getAllCountries())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<ICountry[]>) => {
                    this.countries = response;

                    if (!response.isLoading && response.data) {
                        this.countries.data.forEach((c) => {
                            c.translated = this.$translate.instant(`Entities.Country.${c.alpha2}`);
                        });
                    }
                },
                (error) => {
                    this.countries = error;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public editPartnerSettings() {
        const patchObject = {} as IPartner;

        if (this.defaultCountry.id !== this.original.defaultCountryId) {
            patchObject.defaultCountryId = this.defaultCountry.id;
        }

        if (this.edited.defaultLocale !== this.original.defaultLocale) {
            patchObject.defaultLocale = this.edited.defaultLocale;
        }

        if (this.defaultLanguage.key !== this.original.defaultLanguageKey) {
            patchObject.defaultLanguageKey = this.defaultLanguage.key;
        }

        if (this.edited.defaultTimeZoneName !== this.original.defaultTimeZoneName) {
            patchObject.defaultTimeZoneName = this.edited.defaultTimeZoneName;
        }

        if (this.edited.autoLogoutMinutes !== this.original.autoLogoutMinutes) {
            patchObject.autoLogoutMinutes = this.edited.autoLogoutMinutes;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(this.partnersRepository.patch(this.original.id, patchObject))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<IPartner>) => {
                        this.editResponse = response;
                        if (!response.isLoading && response.data) {
                            this.resolve.options.onOk(response.data);
                        }
                    },
                    (error) => {
                        this.editResponse = error;
                    },
                );
        } else {
            this.cancel();
        }
    }
}
